import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowDown, ArrowRight, ArrowUpRight } from "lucide-react";
import { api, clearJsonLd, setJsonLd, setSeo, track } from "@/lib/api";
import { GuideCard } from "@/components/site/Blocks";
import { CommercialModule } from "@/components/site/CommercialModule";
import { OfferGrid, AffiliateDisclosure } from "@/components/site/OfferGrid";
import { HubExpandedPaths } from "@/components/site/HubExpandedPaths";
import NotFound, { Loading } from "@/pages/NotFound";
import { LoadFailed } from "@/components/site/LoadFailed";
import { SmartImage } from "@/components/site/SmartImage";
import { scrollToSectionId } from "@/lib/scrollToSection";
import { SectionNavMenu } from "@/components/site/SectionNavMenu";

// Scrolls an in-page section clear of the sticky header so its heading is never hidden.
const scrollToSection = (id) => scrollToSectionId(id);

export default function HubPage() {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setMissing(false);
    setFailed(false);
    setHub(null);
    api.get(`/hubs/${slug}`).then((r) => {
      const d = r.data;
      setHub(d);
      setSeo({
        title: d.meta_title || `${d.name} — Human Home Living`,
        description: d.meta_description || d.intro,
      });
      const origin = window.location.origin;
      const url = `${origin}/hub/${slug}`;
      setJsonLd("ld-hub", {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: d.meta_title || `${d.name} — Human Home Living`,
        description: d.meta_description || d.intro,
        url,
        isPartOf: { "@type": "WebSite", name: "Human Home Living", url: `${origin}/` },
      });
      setJsonLd("ld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: d.name, item: url },
        ],
      });
      track({ event_type: "page_view", page: `/hub/${slug}` });
    }).catch((err) => {
      if (err.response?.status === 404) setMissing(true);
      else setFailed(true);
    });
    return () => {
      clearJsonLd("ld-hub");
      clearJsonLd("ld-breadcrumb");
    };
  }, [slug]);

  const featureCtas = hub?.feature?.ctas;
  useEffect(() => {
    if (!featureCtas?.length) return;
    featureCtas.forEach((c) =>
      track({
        event_type: "cta_impression",
        cta_placement: "HUB FEATURE CTA",
        offer_category: hub.feature.offer_category,
        merchant: c.merchant,
      })
    );
  }, [featureCtas]);

  if (failed) return <LoadFailed what="this category" />;
  if (missing)
    return (
      <NotFound
        title="That category doesn't exist"
        body="Human Home Living covers five categories. You'll find all of them in the navigation and the footer."
      />
    );
  if (!hub) return <Loading label="Loading hub" />;

  // A category card can be held back while its affiliate programme is pending. The card keeps all
  // of its configuration in the database and returns the moment the flag is lifted; with only two
  // cards left the row widens them so no empty column is left behind.
  const visiblePaths = (hub.money_paths || []).filter((m) => !m.hidden);
  const pathSpan = visiblePaths.length === 2 ? "lg:col-span-3" : "lg:col-span-2";

  const hasAffiliate =
    Boolean(hub.offer_showcase) || (visiblePaths || []).some((m) => m.offer_category);
  const centeredHero = hub.hero_align === "center";
  const heroImage = hub.hero_image;
  const preview = hub.gallery_preview;
  const galleryPreview = preview ? (
    <section
      data-testid="hub-gallery-preview"
      className="mt-12 md:mt-14 pt-10 md:pt-12 border-t border-line"
    >
      <h2 className="display text-[24px] md:text-[31px] leading-[1.15] text-charcoal max-w-2xl">
        {preview.heading}
      </h2>
      <p className="text-[16.5px] md:text-[17px] leading-[1.65] text-[#454545] mt-3 max-w-2xl">
        {preview.body}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mt-7">
        {preview.images.map((img) => (
          <Link
            key={img.base}
            to={preview.to}
            data-testid={`hub-gallery-preview-image-${img.base.split("/").pop()}`}
            className="group block overflow-hidden aspect-[4/3] bg-bone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2"
          >
            {/* eslint-disable-next-line no-restricted-syntax -- approved partner photo, served as-is */}
            <img
              src={`${img.base}-800.webp`}
              alt={img.alt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
            />
          </Link>
        ))}
      </div>
      <Link
        to={preview.to}
        data-testid="hub-gallery-preview-cta"
        className="mt-7 inline-flex items-center gap-2 px-7 py-4 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
      >
        {preview.cta_label} <ArrowRight size={15} />
      </Link>
    </section>
  ) : null;

  // A single featured product, linking internally to its own page. No affiliate link lives here.
  const product = hub.featured_product;
  const featuredProductBlock = product ? (
    <section
      id={product.anchor_id || undefined}
      data-testid="hub-featured-product"
      className="mt-12 md:mt-14 pt-10 md:pt-12 border-t border-line"
    >
      {product.eyebrow && <span className="eyebrow">{product.eyebrow}</span>}
      {/* Shareable alias so an external link such as #human-touch-pc610 lands on this card too. */}
      {product.anchor_alias && (
        <span id={product.anchor_alias} aria-hidden="true" className="block scroll-mt-28" />
      )}
      <div className="grid sm:grid-cols-[minmax(0,230px)_1fr] gap-6 sm:gap-9 items-center mt-6">
        <Link
          to={product.to}
          data-testid="hub-featured-product-image"
          className="block overflow-hidden bg-white border border-line aspect-square focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2"
        >
          <SmartImage
            src={product.image}
            alt={product.image_alt}
            sizes="(min-width:640px) 230px, 100vw"
            maxWidth={480}
            width={2000}
            height={2000}
            className="w-full h-full object-contain p-4"
          />
        </Link>
        {product.tracking_pixel && (
          /* eslint-disable-next-line no-restricted-syntax -- advertiser impression tracker */
          <img
            src={product.tracking_pixel}
            alt=""
            aria-hidden="true"
            width="1"
            height="1"
            data-testid="hub-featured-product-pixel"
            className="absolute w-px h-px opacity-0 pointer-events-none"
          />
        )}
        <div>
          <h2 className="display text-[24px] md:text-[31px] leading-[1.15] text-charcoal max-w-2xl">
            {product.heading}
          </h2>
          <p className="text-[16.5px] leading-[1.65] text-[#454545] mt-3 max-w-2xl">
            {product.body}
          </p>
          <Link
            to={product.to}
            data-testid="hub-featured-product-cta"
            className="mt-6 inline-flex items-center gap-2 px-7 py-4 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
          >
            {product.cta_label} <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </section>
  ) : null;

  const hubInserts = [
    preview ? { id: preview.after_group, key: "gallery", node: galleryPreview } : null,
    product ? { id: product.after_group, key: "product", node: featuredProductBlock } : null,
  ].filter(Boolean);

  // A hub can ask for its editorial feature to sit lower down the page, after a named group of
  // expanded paths, so an ad landing page leads with the category the campaign is promoting.
  const groups = hub.expanded_paths || [];
  const featureAfter =
    hub.feature?.after_group && groups.some((g) => g.id === hub.feature.after_group)
      ? hub.feature.after_group
      : null;
  const splitAt = featureAfter ? groups.findIndex((g) => g.id === featureAfter) + 1 : 0;
  const groupsBeforeFeature = featureAfter ? groups.slice(0, splitAt) : [];
  const groupsAfterFeature = featureAfter ? groups.slice(splitAt) : groups;

  const featureBlock = hub.feature ? (
    <div
      id={hub.feature.anchor_id || undefined}
      data-testid="hub-feature"
      className="mt-12 md:mt-14 pt-10 md:pt-12 border-t border-line"
    >
      {hub.feature.eyebrow && (
        <span className="eyebrow" data-testid="hub-feature-eyebrow">
          {hub.feature.eyebrow}
        </span>
      )}
      <div
        className={`grid lg:grid-cols-[58fr_42fr] gap-7 lg:gap-12 items-center ${
          hub.feature.eyebrow ? "mt-7" : ""
        }`}
      >
      <div className="overflow-hidden aspect-[16/11] lg:aspect-auto lg:h-[460px]">
        <SmartImage
          src={hub.feature.image}
          alt={hub.feature.image_alt}
          sizes="(min-width:1024px) 58vw, 100vw"
          maxWidth={1600}
          className="w-full h-full object-cover"
        />
      </div>
      <div>
        <h2
          data-testid="hub-feature-heading"
          className="display text-[26px] md:text-[33px] leading-[1.14] text-charcoal"
        >
          {hub.feature.heading}
        </h2>
        <p className="text-[16.5px] md:text-[17.5px] leading-[1.6] text-[#454545] mt-3.5 max-w-md">
          {hub.feature.body}
        </p>
        <div className="mt-7">
          {hub.feature.ctas?.length ? (
            <div data-testid="hub-feature-ctas">
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-5">
                {hub.feature.ctas.map((c) => (
                  <div key={c.merchant} className="w-full sm:w-auto">
                  {c.note && (
                    <p
                      data-testid={`hub-feature-cta-note-${c.merchant
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                      className="text-[13px] leading-[1.55] text-[#6E6A63] mb-2.5 max-w-[22rem]"
                    >
                      {c.note}
                    </p>
                  )}
                  <a
                    href={c.tracking_url}
                    rel="sponsored nofollow noopener"
                    target="_blank"
                    onClick={() =>
                      track({
                        event_type: "cta_click",
                        cta_placement: "HUB FEATURE CTA",
                        offer_category: hub.feature.offer_category,
                        offer_id: c.offer_id,
                        merchant: c.merchant,
                      })
                    }
                    data-testid={`hub-feature-cta-${c.merchant.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-8 py-5 text-[13.5px] tracking-[0.13em] bg-sage text-white uppercase font-semibold hover:bg-sage-dark transition-colors duration-300 shadow-sm hover:shadow-md"
                  >
                    {c.label} <ArrowRight size={17} />
                  </a>
                  </div>
                ))}
              </div>
              <p className="text-[12px] mt-3 text-[#6E6A63]">
                Affiliate links · open at{" "}
                <span className="font-semibold text-[#3F3F3F]">
                  {hub.feature.ctas.map((c) => c.merchant).join(" and ")}
                </span>
              </p>
              <AffiliateDisclosure
                testId="hub-feature-disclosure"
                className="mt-3 text-[11.5px]"
              />
            </div>
          ) : (
            <CommercialModule
              category={hub.feature.offer_category}
              label={hub.feature.cta_label}
              placement="HUB FEATURE CTA"
              testKey="hub-feature-massage-chairs"
              compact
              allowLive
            />
          )}
        </div>
      </div>
      </div>
    </div>
  ) : null;

  return (
    <div data-testid={`hub-page-${slug}`}>
      <section
        className={`relative bg-charcoal ${
          heroImage ? "h-[58vh] min-h-[430px] max-h-[720px]" : "h-[46vh] min-h-[340px]"
        }`}
      >
        {heroImage ? (
          /* Supplied partner photograph, served from /media as pre-generated WebP derivatives.
             Stationary by design: no video, no motion, no transitions. */
          /* eslint-disable-next-line no-restricted-syntax -- approved partner photo, served as-is */
          <img
            src={`${heroImage.base}-1600.webp`}
            srcSet={`${heroImage.base}-800.webp 800w, ${heroImage.base}-1600.webp 1600w`}
            sizes="100vw"
            alt={heroImage.alt}
            fetchPriority="high"
            data-testid="hub-hero-image"
            className="absolute inset-0 w-full h-full object-cover object-[50%_18%] md:object-[50%_22%]"
          />
        ) : (
          <SmartImage
            src={hub.image}
            alt={hub.image_alt || hub.name}
            priority
            sizes="100vw"
            className={`absolute inset-0 w-full h-full ${
              hub.hero_fit === "contain-mobile" ? "object-contain sm:object-cover" : "object-cover"
            }`}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/88 via-black/55 to-black/20" />
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/55 to-transparent" />
        <div
          className={`relative h-full max-w-[1280px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-12 text-white ${
            centeredHero ? "items-center text-center" : ""
          }`}
        >
          <nav className="overlay-copy text-[12.5px] font-medium text-white/90 mb-5">
            <Link to="/" className="hover:text-white">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span>{hub.name}</span>
          </nav>
          <h1
            className={`overlay-title text-[40px] md:text-[58px] leading-[1.03] max-w-3xl ${
              centeredHero ? "mx-auto" : ""
            }`}
          >
            {hub.name}
          </h1>
          <p
            className={`overlay-copy text-[19px] md:text-[24px] font-light mt-4 text-white/95 ${
              centeredHero ? "mx-auto max-w-3xl" : "max-w-2xl"
            }`}
          >
            {hub.positioning}
          </p>
        </div>
      </section>

      {hub.section_nav?.links?.length > 0 && (
        <nav
          data-testid="hub-section-nav"
          aria-label={hub.section_nav.heading}
          className="border-b border-line bg-bone/70"
        >
          <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-3.5 md:gap-7">
            <span className="eyebrow whitespace-nowrap">{hub.section_nav.heading}</span>
            <div className="flex flex-wrap gap-2.5 md:gap-3">
              {hub.section_nav.links.map((l) =>
                l.children?.length > 0 ? (
                  <SectionNavMenu
                    key={l.target}
                    label={l.label}
                    items={l.children}
                    onSelect={scrollToSection}
                  />
                ) : (
                  <button
                    key={l.target}
                    type="button"
                    onClick={() => scrollToSection(l.target)}
                    data-testid={`hub-section-nav-${l.target}`}
                    className="rounded-full border border-charcoal/15 bg-white px-4 py-2 text-[13px] md:text-[13.5px] font-medium text-charcoal hover:border-sage hover:bg-sage hover:text-white transition-colors duration-300"
                  >
                    {l.label}
                  </button>
                )
              )}
            </div>
          </div>
        </nav>
      )}

      <section className="px-5 md:px-8 py-14 md:py-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="max-w-3xl">
            <span className="eyebrow">About this category</span>
            <p className="text-[18px] md:text-[22px] font-light leading-[1.55] text-[#3F3F3F] mt-5">
              {hub.intro}
            </p>
            {hasAffiliate && (
              <AffiliateDisclosure
                testId="page-affiliate-disclosure"
                className="mt-6 border-l-2 border-sage/50 pl-4"
              />
            )}
          </div>

          {!featureAfter && featureBlock}

          <div className="mt-12 md:mt-16" id="explore-your-options">
            <span className="eyebrow">Explore Your Options</span>
            {/* Image-led category cards: three across, then the remaining pair centred. The
                photograph and title forward their click to the card's own CTA, so every
                destination and its tracking stay exactly as configured. */}
            <div className="grid lg:grid-cols-6 gap-7 md:gap-8 mt-7">
              {visiblePaths.map((m, i) => (
                <div
                  key={m.name}
                  id={m.offer_category ? `option-${m.offer_category}` : undefined}
                  data-card
                  data-testid={`hub-option-card-${m.offer_category || i}`}
                  className={`group flex flex-col bg-white border border-line transition-shadow duration-300 ${pathSpan} ${
                    i === 3 && visiblePaths.length > 3 ? "lg:col-start-2" : ""
                  } ${
                    m.nav_buttons?.length > 1
                      ? ""
                      : "cursor-pointer hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.45)]"
                  }`}
                  onClick={(e) => {
                    // The card's own CTA (or a nav button) handles its own click; everywhere else on
                    // the card forwards to that single destination, so one click = one event.
                    if (e.target.closest("a, button")) return;
                    if (m.nav_buttons?.length === 1) {
                      scrollToSection(m.nav_buttons[0].target);
                      return;
                    }
                    if (!m.nav_buttons)
                      e.currentTarget.querySelector("a")?.click();
                  }}
                >
                  <div>
                    {/* Only hubs whose categories have approved photography get an image area.
                        Without an image the card stays text-only rather than showing an empty box. */}
                    {m.image && (
                      <div
                        className={`overflow-hidden aspect-[4/3] ${
                          m.image_fit === "contain" ? "bg-bone p-4" : "bg-bone"
                        }`}
                      >
                        <SmartImage
                          src={m.image}
                          alt={m.image_alt || m.name}
                          sizes="(min-width:1024px) 33vw, 100vw"
                          maxWidth={1024}
                          className={`w-full h-full ${
                            m.image_fit === "contain" ? "object-contain" : "object-cover"
                          }`}
                        />
                      </div>
                    )}
                    <h3 className="display text-[22px] md:text-[25px] leading-[1.15] text-charcoal px-6 pt-6 group-hover:text-sage-dark transition-colors duration-300">
                      {m.name}
                    </h3>
                  </div>
                  <p className="text-[15.5px] md:text-[16.5px] text-[#454545] mt-2.5 px-6 leading-[1.6]">
                    {m.note}
                  </p>
                  {m.nav_buttons?.length > 0 ? (
                    <div
                      data-testid={`hub-option-nav-${m.offer_category || i}`}
                      className="mt-auto px-6 pt-6 pb-7 flex flex-col gap-2.5"
                    >
                      {m.nav_buttons.map((b) => (
                        <button
                          key={b.target + b.label}
                          type="button"
                          onClick={() => scrollToSection(b.target)}
                          data-testid={`hub-option-nav-btn-${b.target}`}
                          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
                        >
                          {b.label} <ArrowDown size={15} />
                        </button>
                      ))}
                    </div>
                  ) : (
                    m.offer_category && (
                      <div className="mt-auto px-6 pt-6 pb-7">
                        <CommercialModule
                          compact
                          allowLive
                          showDisclosure={false}
                          category={m.offer_category}
                          testKey={`hub-option-${m.offer_category}`}
                          label={m.cta_label || "Explore Options"}
                          anchorTo={
                            m.anchor_to ||
                            (hub.offer_showcase?.category === m.offer_category
                              ? `#explore-${m.offer_category}`
                              : null)
                          }
                          placement="HUB CATEGORY CTA"
                        />
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          </div>

          {groupsBeforeFeature.length > 0 && (
            <HubExpandedPaths
              groups={groupsBeforeFeature}
              insertAfter={hubInserts}
            />
          )}

          {featureAfter && featureBlock}

          {groupsAfterFeature.length > 0 && (
            <HubExpandedPaths
              groups={groupsAfterFeature}
              testId="hub-expanded-paths-secondary"
              insertAfter={hubInserts}
            />
          )}

          {hub.offer_showcase && (
            <div className="max-w-[1080px]">
              <OfferGrid
                category={hub.offer_showcase.category}
                heading={hub.offer_showcase.heading}
                intro={hub.offer_showcase.intro}
                id={`explore-${hub.offer_showcase.category}`}
                placement="HUB OFFER GRID"
              />
            </div>
          )}

          {hub.generic_cta !== false && (
            <div className="max-w-[1080px]">
              <CommercialModule
                category={hub.offer_showcase?.category || visiblePaths?.[0]?.offer_category || hub.slug}
                label="See Current Options"
                headline={`Already know what you're looking for?`}
                note="You don't need to read a guide or use a planning tool first. Jump straight to the category you care about and see what is available."
                placement="EARLY CTA"
                anchorTo={
                  hub.offer_showcase
                    ? `#explore-${hub.offer_showcase.category}`
                    : "#explore-your-options"
                }
              />
            </div>
          )}
        </div>
      </section>

      {hub.tools?.length > 0 && (
        <section className="bg-charcoal text-white px-5 md:px-8 py-16 md:py-20">
          <div className="max-w-[1280px] mx-auto">
            <span className="eyebrow eyebrow-light">Planning tools</span>
            <div className="grid md:grid-cols-2 gap-px bg-white/12 mt-8">
              {hub.tools.map((t) => (
                <Link
                  key={t.slug}
                  to={`/tools/${t.slug}`}
                  data-testid={`hub-tool-${t.slug}`}
                  className="group bg-charcoal p-8 hover:bg-[#3C3C3C] transition-colors duration-500"
                >
                  <h3 className="display text-[26px] leading-tight">{t.name}</h3>
                  <p className="text-[16px] md:text-[17px] leading-[1.6] text-[#CBC8C2] mt-3">{t.summary}</p>
                  <span className="inline-flex items-center gap-2 text-[12.5px] tracking-[0.14em] uppercase font-semibold mt-6 group-hover:gap-3.5 transition-all">
                    Open tool <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-5 md:px-8 pb-16 md:pb-24 pt-4 md:pt-8">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="display text-[32px] md:text-[42px] leading-tight text-charcoal mb-10">
            Decision guides
          </h2>
          <div
            data-testid="hub-articles"
            className={`grid gap-6 lg:gap-7 ${
              (hub.articles || []).length < 3 ? "md:grid-cols-2 max-w-3xl" : "md:grid-cols-2 lg:grid-cols-3"
            }`}
          >
            {(hub.articles || []).map((a, i) => (
              <GuideCard key={a.slug} a={a} index={i} />
            ))}
          </div>
          {hub.articles?.length === 0 && (
            <p className="text-[17px] text-[#454545]">
              Guides for this category are on the way. In the meantime, our planning tools can help
              you work out what your home supports.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
